import React, { useEffect, useMemo, useState } from "react";
import "./ProductCard.css";
import { Product, ProductVariant } from "./types";
import { useTenant } from "../../contexts/TenantContext";
import { useTranslation } from "react-i18next";

type Props = {
    product: Product;
    onAdd: (product: Product, variant?: ProductVariant) => void;
    onRemove?: (product: Product, variant?: ProductVariant) => void;
    baseQuantity?: number;
    variantQuantities?: Record<string, number>;
    orderingEnabled?: boolean;
};

const ProductCard: React.FC<Props> = ({
    product,
    onAdd,
    onRemove,
    baseQuantity = 0,
    variantQuantities = {},
    orderingEnabled = true,
}) => {
    const { tenantInfo } = useTenant();
    const { t } = useTranslation();

    const getImageSrc = () => {
        // Priority: product image > tenant logo > default placeholder
        if (product.image) {
            return product.image;
        }
        if (tenantInfo?.logoUrl) {
            return tenantInfo.logoUrl;
        }
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNhYWFhYWEiPkltbWFnaW5lIG5vbiBkaXNwb25pYmlsZTwvdGV4dD48L3N2Zz4=';
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const target = e.currentTarget;

        // If the current src is already the tenant logo or default, don't retry
        if (target.src === tenantInfo?.logoUrl || target.src.includes('data:image/svg+xml')) {
            target.style.opacity = '0.6';
            target.style.filter = 'grayscale(20%)';
            return;
        }

        // Try tenant logo as fallback
        if (tenantInfo?.logoUrl && target.src !== tenantInfo.logoUrl) {
            target.src = tenantInfo.logoUrl;
            target.style.opacity = '0.7';
            target.style.filter = 'grayscale(30%)';
            return;
        }

        // Final fallback
        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNhYWFhYWEiPkltbWFnaW5lIG5vbiBkaXNwb25pYmlsZTwvdGV4dD48L3N2Zz4=';
        target.onerror = null;
    };

    const safeVariants = useMemo(() => (product.variants || []).filter(variant => variant.available !== false), [product.variants]);
    const showVariantDropdown = safeVariants.length > 1;
    const hasSelectedExtras = useMemo(() => safeVariants.some(variant => {
        const variantKey = String(variant.id);
        return (variantQuantities[variantKey] || 0) > 0;
    }), [safeVariants, variantQuantities]);
    const [variantsExpanded, setVariantsExpanded] = useState(() => safeVariants.length > 0 && (!showVariantDropdown || hasSelectedExtras));
    useEffect(() => {
        if (safeVariants.length === 0) {
            setVariantsExpanded(false);
            return;
        }
        if (hasSelectedExtras) {
            setVariantsExpanded(true);
            return;
        }
        setVariantsExpanded(prev => (prev ? prev : !showVariantDropdown));
    }, [product.id, safeVariants.length, showVariantDropdown, hasSelectedExtras]);

    const baseInCart = baseQuantity > 0;
    const variantActionsDisabled = !orderingEnabled || !baseInCart;

    const toggleVariants = () => {
        setVariantsExpanded(prev => !prev);
    };

    return (
        <div className="product-card">
            <img
                src={getImageSrc()}
                alt={product.name}
                className="product-image"
                onError={handleImageError}
            />
            <div className="product-info">
                <h3 className="product-title">{product.name}</h3>
                {product.description && (
                    <p className="product-desc">{product.description}</p>
                )}
                <div className="product-bottom">
                    <span className="product-price">
                        €{product.price.toFixed(2)}
                    </span>
                    <div className="product-actions">
                        {orderingEnabled && baseInCart && onRemove && (
                            <button
                                className="remove-btn"
                                onClick={() => onRemove(product)}
                                disabled={baseQuantity === 0}
                                aria-label="Rimuovi dal carrello"
                            >
                                −
                            </button>
                        )}
                        {orderingEnabled && baseInCart && <span className="product-qty">{baseQuantity}</span>}
                        {orderingEnabled && (
                            <button
                                className="add-btn"
                                onClick={() => onAdd(product)}
                                aria-label="Aggiungi al carrello"
                            >
                                +
                            </button>
                        )}
                    </div>
                </div>
                {safeVariants.length > 0 && (
                    <div className="variant-list">
                        <div className="variant-list__header">
                            <span className="variant-list__title">
                                {safeVariants.length > 1
                                    ? t('menu.product.extras.title')
                                    : t('menu.product.extras.titleSingular')}
                            </span>
                            {showVariantDropdown && (
                                <button
                                    type="button"
                                    className="variant-toggle"
                                    onClick={toggleVariants}
                                    aria-expanded={variantsExpanded}
                                >
                                    {variantsExpanded
                                        ? t('menu.product.extras.toggleHide')
                                        : t('menu.product.extras.toggleShow', { count: safeVariants.length })}
                                </button>
                            )}
                        </div>
                        {variantsExpanded && (
                            <div className="variant-rows">
                                {safeVariants.map(variant => {
                                    const variantKey = String(variant.id);
                                    const qty = variantQuantities[variantKey] || 0;
                                    const inCart = qty > 0;
                                    const disableActions = !orderingEnabled;
                                    return (
                                        <div className="variant-row" key={variantKey}>
                                            <div className="variant-info">
                                                <span className="variant-name">{variant.name}</span>
                                                <span className="variant-price">+€{variant.price.toFixed(2)}</span>
                                            </div>
                                            <div className="variant-actions">
                                                {onRemove && (
                                                    <button
                                                        className="remove-btn"
                                                        onClick={() => onRemove(product, variant)}
                                                        disabled={disableActions || !inCart}
                                                        aria-label={`Rimuovi extra ${variant.name}`}
                                                    >
                                                        −
                                                    </button>
                                                )}
                                                {inCart && <span className="product-qty">{qty}</span>}
                                                <button
                                                    className="add-btn"
                                                    onClick={() => onAdd(product, variant)}
                                                    disabled={variantActionsDisabled}
                                                    aria-label={`Aggiungi extra ${variant.name}`}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {!baseInCart && orderingEnabled && (
                            <div className="variant-hint">
                                {t('menu.product.extras.requireBase', 'Aggiungi prima il prodotto base per selezionare gli extra.')}
                            </div>
                        )}
                        {!variantsExpanded && showVariantDropdown && (
                            <div className="variant-preview">
                                {safeVariants.slice(0, 2).map(variant => (
                                    <span key={String(variant.id)} className="variant-preview__chip">{variant.name}</span>
                                ))}
                                {safeVariants.length > 2 && <span className="variant-preview__chip">+{safeVariants.length - 2}</span>}
                            </div>
                        )}
                        {!showVariantDropdown && variantsExpanded && safeVariants.length === 1 && (
                            <div className="variant-hint">{t('menu.product.extras.singleHint')}</div>
                        )}
                        {showVariantDropdown && variantsExpanded && safeVariants.length > 1 && (
                            <div className="variant-hint">{t('menu.product.extras.multiHint')}</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductCard;